import { Button } from "../ui/button";
import { Input } from "../ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "../ui/dialog";
import { Banknote } from "lucide-react";

function renderSubmitLabel(submitting, isPartial) {
  if (submitting) {
    return (
      <span className="flex items-center gap-2">
        <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
        Saving...
      </span>
    );
  }
  return isPartial ? "Record Partial Payment" : "Confirm Full Payment";
}

const MarkPaidDialog = ({ dialog, setDialog, onSubmit }) => {
  const quote = dialog.quote;
  const subtotal = quote?.subtotal || 0;
  const isPartial = dialog.paymentType === "partial";
  const entered = parseFloat(dialog.amount);
  const hasAmount = !Number.isNaN(entered) && entered > 0;
  const discount = !isPartial && hasAmount && entered < subtotal ? subtotal - entered : 0;
  const overage = !isPartial && hasAmount && entered > subtotal ? entered - subtotal : 0;
  const remaining = isPartial && hasAmount ? Math.max(subtotal - entered, 0) : 0;

  return (
    <Dialog open={dialog.open} onOpenChange={(open) => setDialog((p) => ({ ...p, open }))}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Banknote className="w-5 h-5 text-[#00341d]" />
            Mark as Paid
          </DialogTitle>
          <DialogDescription>
            Record a cash or check payment for {quote?.quote_number} &mdash; {quote?.customer_name}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-5 mt-2">
          {/* Payment Method */}
          <div className="flex gap-3">
            <button
              onClick={() => setDialog((p) => ({ ...p, method: "check" }))}
              className={`flex-1 py-3 px-4 rounded-lg border-2 text-center font-semibold transition-all ${dialog.method === "check" ? "border-[#00341d] bg-[#00341d]/5 text-[#00341d]" : "border-zinc-200 text-zinc-500 hover:border-zinc-300"}`}
              data-testid="payment-method-check"
            >
              Check
            </button>
            <button
              onClick={() => setDialog((p) => ({ ...p, method: "cash", checkNumber: "" }))}
              className={`flex-1 py-3 px-4 rounded-lg border-2 text-center font-semibold transition-all ${dialog.method === "cash" ? "border-[#00341d] bg-[#00341d]/5 text-[#00341d]" : "border-zinc-200 text-zinc-500 hover:border-zinc-300"}`}
              data-testid="payment-method-cash"
            >
              Cash
            </button>
          </div>

          {dialog.method === "check" && (
            <div>
              <label className="text-sm font-medium text-zinc-700 block mb-1.5">Check Number</label>
              <Input
                placeholder="Enter check #"
                value={dialog.checkNumber}
                onChange={(e) => setDialog((p) => ({ ...p, checkNumber: e.target.value }))}
                data-testid="check-number-input"
                autoFocus
              />
            </div>
          )}

          {/* Full vs Partial Payment */}
          <div>
            <label className="text-sm font-medium text-zinc-700 block mb-2">Payment Amount</label>
            <div className="flex gap-3">
              <button
                onClick={() => setDialog((p) => ({ ...p, paymentType: "full", amount: String(subtotal) }))}
                className={`flex-1 py-2.5 px-4 rounded-lg border-2 text-center text-sm font-semibold transition-all ${!isPartial ? "border-[#00341d] bg-[#00341d]/5 text-[#00341d]" : "border-zinc-200 text-zinc-500 hover:border-zinc-300"}`}
                data-testid="payment-type-full"
              >
                Paid in Full
              </button>
              <button
                onClick={() => setDialog((p) => ({ ...p, paymentType: "partial", amount: "" }))}
                className={`flex-1 py-2.5 px-4 rounded-lg border-2 text-center text-sm font-semibold transition-all ${isPartial ? "border-[#d97706] bg-[#d97706]/5 text-[#d97706]" : "border-zinc-200 text-zinc-500 hover:border-zinc-300"}`}
                data-testid="payment-type-partial"
              >
                Balance Due
              </button>
            </div>
          </div>

          <div>
            <label className="text-sm font-medium text-zinc-700 block mb-1.5">Amount Paid</label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400 font-medium">$</span>
              <Input
                type="number"
                step="0.01"
                min="0.01"
                placeholder={subtotal ? subtotal.toFixed(2) : "0.00"}
                value={dialog.amount}
                onChange={(e) => setDialog((p) => ({ ...p, amount: e.target.value }))}
                className="pl-7"
                data-testid="partial-amount-input"
              />
            </div>
            {!isPartial && (
              <p className="text-xs text-zinc-400 mt-1">Adjust if the customer paid a different amount than the quote total.</p>
            )}
          </div>

          {/* Summary */}
          <div className="bg-zinc-50 rounded-lg p-4 space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-zinc-500">Quote Total</span>
              <span className="font-semibold">${subtotal.toLocaleString()}</span>
            </div>
            {isPartial && dialog.amount && (
              <>
                <div className="flex justify-between text-sm">
                  <span className="text-zinc-500">Amount Being Paid</span>
                  <span className="font-semibold text-[#d97706]">${parseFloat(dialog.amount).toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-sm pt-2 border-t border-zinc-200">
                  <span className="text-zinc-500">Remaining Balance</span>
                  <span className="font-semibold text-red-600" data-testid="mark-paid-remaining">
                    ${(subtotal - parseFloat(dialog.amount)).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                  </span>
                </div>
              </>
            )}
            {!isPartial && discount > 0 && (
              <div className="flex justify-between text-sm">
                <span className="text-zinc-500">Discount Applied</span>
                <span className="font-semibold text-[#d97706]" data-testid="mark-paid-discount">
                  -${discount.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                </span>
              </div>
            )}
            {!isPartial && overage > 0 && (
              <div className="flex justify-between text-sm">
                <span className="text-zinc-500">Overage Recorded</span>
                <span className="font-semibold text-green-600" data-testid="mark-paid-overage">
                  +${overage.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                </span>
              </div>
            )}
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <Button variant="outline" onClick={() => setDialog((p) => ({ ...p, open: false }))}>Cancel</Button>
            <Button
              onClick={onSubmit}
              disabled={dialog.submitting || (isPartial && (!dialog.amount || parseFloat(dialog.amount) <= 0))}
              className={isPartial ? "bg-[#d97706] hover:bg-[#b45309] text-white" : "bg-[#00341d] hover:bg-[#002816] text-white"}
              data-testid="confirm-mark-paid"
            >
              {renderSubmitLabel(dialog.submitting, isPartial)}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default MarkPaidDialog;
